/**
 * Transmittals, as the issuer works them (`MCK-20260812-010`): the issue drawer, the register of
 * dispatches and one transmittal's detail. The recipient's own page is `share.ts`'s
 * `transmittal.*`, and the two share their words — see `docs/domain/ui-vocabulary.md`.
 *
 * **The tracking vocabulary is five words and no sixth**: sent, delivered, opened, downloaded, and
 * a failed delivery. Never "read", never "acknowledged" — the product records a link followed,
 * not a person having understood a drawing (§"What proof of delivery can and cannot mean").
 * `backend/tests/jobs/test_delivery_vocabulary.py` sweeps this file in all three languages.
 *
 * `role.*` is keyed by the wire's role names (`issued_by_role_at_issue`), which are frozen at issue.
 */
export const transmittalsEn = {
  'page.title': 'Transmittals',
  'page.subtitle':
    'What left the studio: which versions went to whom, when, and what happened to each message.',
  'export.csv': 'Download CSV',

  'column.number': 'Number',
  'column.issuedAt': 'Issued',
  'column.reason': 'Reason for issue',
  'column.phase': 'Phase',
  'column.items': 'Drawings',
  'column.recipients': 'Recipients',
  'column.links': 'Corrections',

  'list.explains': 'A transmittal records which versions you sent to whom, and why.',
  'list.emptyTitle': 'Nothing issued yet',
  'list.emptyPhaseTitle': 'Nothing issued in this phase',
  'list.empty': 'Nothing has been issued from this project yet. Select drawings in the register and press Issue.',
  'list.emptyPhase': 'Nothing was issued in this phase.',
  'list.failed': 'The transmittals could not be loaded. Try again.',
  'list.more': 'Show more',
  'list.loading': 'Loading transmittals…',
  'list.corrects': 'Corrects {label}',
  'list.correctedBy': 'Corrected by {label}',
  'list.correctsEarlier': 'Corrects an earlier transmittal',
  'list.correctedByLater': 'Corrected by a later transmittal',
  'list.noReason': 'No reason recorded',

  // The drawer. Composition is client state: nothing exists until Issue is pressed, and then the
  // record exists whole.
  'issue.title': 'Issue a transmittal',
  'issue.close': 'Close',
  'issue.drawings': '{count, plural, one {# drawing} other {# drawings}}',
  // Rows ticked in the register whose drawing has no published version yet: there is nothing to
  // send for them, and saying so beats sending a smaller set in silence.
  'issue.unpublished':
    '{count, plural, one {# selected drawing has} other {# selected drawings have}} no published version and will not be sent.',
  'issue.nothingToSend': 'None of the selected drawings has a published version. Publish one first.',
  'issue.recipients': 'Recipients',
  'issue.member': 'Project member',
  'issue.memberPick': 'Choose a person',
  'issue.add': 'Add',
  'issue.contact': 'Party contact',
  'issue.contactPick': 'Choose a contact',
  'issue.contactNoEmail': 'no e-mail',
  'issue.email': 'E-mail',
  'issue.displayName': 'Name on the record',
  'issue.displayNameHint': 'Printed on the delivery record. Optional.',
  'issue.emailInvalid': 'Enter an e-mail address.',
  'issue.duplicate': 'This recipient is already on the list.',
  'issue.noRecipients': 'Add at least one recipient.',
  'issue.remove': 'Remove {name}',
  'issue.withoutAccount': 'No account. Gets a personal link.',
  'issue.reason': 'Reason for issue',
  'issue.reasonPick': 'Choose a reason',
  'issue.reasonRequired': 'Choose why this set is being issued.',
  'issue.noReasons': 'This project has no reasons for issue yet.',
  'issue.phase': 'Phase',
  'issue.phaseCurrent': '{phase}. The project’s current phase is recorded with the issue.',
  'issue.phaseNone': 'The project has no current phase, so none is recorded.',
  'issue.message': 'Message',
  'issue.messageHint': 'Goes in the e-mail to every recipient.',
  'issue.supersedes': 'Corrects',
  'issue.supersedesNone': 'Nothing. This is a new issue.',
  'issue.immutable':
    'A transmittal cannot be edited once issued. A mistake is corrected by issuing a new one that corrects it.',
  'issue.submit': 'Issue',
  'issue.cancel': 'Cancel',
  // Above the server's own sentence on a refusal. The server names who, which drawings and the
  // ways forward; this line only says that nothing happened.
  'issue.refused': 'Nothing was issued.',
  'issue.failed': 'The transmittal could not be issued. Try again.',

  'detail.back': 'Back to transmittals',
  'detail.missing': 'This transmittal does not exist, or it is not visible to you.',
  'detail.failed': 'The transmittal could not be loaded. Try again.',
  'detail.issuedBy': 'Issued by {name} as {role}',
  'detail.issuedAs': 'Issued as {role}',
  'detail.issuedOn': 'Issued {date}',
  'detail.reason': 'Reason for issue: {reason}',
  'detail.phase': 'Phase: {phase}',
  'detail.message': 'Message',
  'detail.corrects': 'Corrects {label}',
  'detail.correctedBy': 'Corrected by {label}',
  'detail.deliveryRecord': 'Delivery record (PDF)',
  'detail.refresh': 'Refresh',
  'detail.loading': 'Loading the transmittal…',
  'detail.items': 'Drawings sent',
  'detail.recipients': 'Recipients',

  'item.drawing': 'Drawing',
  'item.title': 'Title',
  'item.version': 'Version',
  'item.file': 'File',
  'item.noFile': 'No file',
  // An annotation, never a substitution: the row still names what was sent.
  'item.current': 'issued {issued} · current is {current}',

  'recipient.name': 'Recipient',
  'recipient.state': 'State',
  'recipient.sent': 'Sent',
  'recipient.delivered': 'Delivered',
  'recipient.opened': 'Opened',
  'recipient.downloaded': 'Downloaded',
  'recipient.actions': 'Actions',
  'recipient.member': 'Project member',
  'recipient.erased': 'Recipient erased (ref {ref})',
  'recipient.unnamed': 'Recipient',
  'recipient.revoke': 'Revoke access',
  'recipient.renotify': 'Send again',
  'recipient.revokeConfirm':
    'Revoke access for {name}? Their link stops working at once. The record of what was sent stays.',
  'recipient.confirm': 'Revoke',
  'recipient.cancel': 'Keep',
  'recipient.revoked': 'Access revoked. The record is unchanged.',
  'recipient.renotified': 'Sent again. A fresh link replaces the old one.',
  'recipient.renotifiedMember': 'Sent again.',
  'recipient.actionFailed': 'That did not work. Try again.',

  'state.pending': 'Not sent yet',
  'state.sent': 'Sent',
  'state.delivered': 'Delivered',
  'state.failed': 'Delivery failed',
  'state.opened': 'Opened',
  'state.downloaded': 'Downloaded',

  'role.admin': 'admin',
  'role.manager': 'manager',
  'role.architect': 'architect',
  'role.engineer': 'engineer',
  'role.contractor': 'contractor',
  'role.client_readonly': 'client',
} as const;
